import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Download, Loader2, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Weekly Wrapped — a full-screen, Instagram/Spotify-Wrapped-style story.
 *
 * Segmented auto-advancing progress bars up top, tap-left/right to move,
 * press-and-hold to pause. On-brand (paper/brass/forest) deep gradient
 * slides with Cormorant display numerals. The final slide summarises the
 * week and offers Download as image / PDF.
 */
const DURATION = 6000;       // ms per auto-advance slide
const INTRO_DURATION = 4200;

const ACCENTS = {
  forest: "linear-gradient(165deg, #12352550 0%, #0b1a12 55%, #081109 100%)",
  brass: "linear-gradient(165deg, #4a351260 0%, #1a1610 55%, #100d09 100%)",
  saddle: "linear-gradient(165deg, #3a241660 0%, #17110b 55%, #100b07 100%)",
  olive: "linear-gradient(165deg, #2b301660 0%, #14150c 55%, #0c0d07 100%)",
  night: "linear-gradient(165deg, #1a1510 0%, #0d0b08 60%, #080706 100%)",
};

export function WeeklyWrap({ open, data, onClose, onSeen }) {
  const slides = useMemo(() => {
    if (!data) return [];
    return [{ id: "intro", type: "intro" }, ...(data.slides || []), { id: "summary", type: "summary" }];
  }, [data]);

  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const elapsedRef = useRef(0);
  const seenRef = useRef(false);

  const cur = slides[i];
  const isSummary = cur?.type === "summary";
  const dur = cur?.type === "intro" ? INTRO_DURATION : DURATION;

  const goNext = useCallback(() => {
    setI((p) => Math.min(p + 1, slides.length - 1));
  }, [slides.length]);
  const goPrev = useCallback(() => setI((p) => Math.max(p - 1, 0)), []);

  // Reset timing when slide changes.
  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
    startRef.current = performance.now();
  }, [i]);

  // rAF progress loop (summary never auto-advances).
  useEffect(() => {
    if (!open || isSummary) return;
    cancelAnimationFrame(rafRef.current);
    startRef.current = performance.now() - elapsedRef.current;
    const tick = (now) => {
      if (paused) { startRef.current = now - elapsedRef.current; rafRef.current = requestAnimationFrame(tick); return; }
      elapsedRef.current = now - startRef.current;
      const pr = Math.min(1, elapsedRef.current / dur);
      setProgress(pr);
      if (pr >= 1) {
        if (i >= slides.length - 1) return;
        goNext();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, i, paused, isSummary, dur, goNext, slides.length]);

  // Mark seen once (best-effort) + reset index on open.
  useEffect(() => {
    if (open) {
      setI(0); setPaused(false);
      if (!seenRef.current) { seenRef.current = true; onSeen?.(); }
    } else {
      seenRef.current = false;
    }
  }, [open, onSeen]);

  // Keyboard nav.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") onClose?.();
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, goNext, goPrev, onClose]);

  if (!open || !data) return null;

  const accent = ACCENTS[cur?.accent] || ACCENTS.night;

  // Press-and-hold to pause; quick tap navigates by side.
  let downAt = 0;
  const onPointerDown = () => { downAt = performance.now(); setPaused(true); };
  const onPointerUp = (e) => {
    const held = performance.now() - downAt;
    setPaused(false);
    if (held < 220) {
      const x = e.clientX ?? (e.changedTouches?.[0]?.clientX || 0);
      if (x < window.innerWidth * 0.32) goPrev(); else goNext();
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[300] flex items-stretch justify-center"
      style={{ background: "rgba(8,7,6,0.92)" }}
      data-testid="weekly-wrap"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-[520px] h-full overflow-hidden" style={{ background: accent, transition: "background 500ms ease" }}>
        {/* Segmented progress */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 px-3 pt-3" data-testid="wrap-progress">
          {slides.map((_, idx) => (
            <div key={idx} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.25)" }}>
              <div style={{ height: "100%", background: "#f7f4ed", width: idx < i ? "100%" : idx === i ? `${progress * 100}%` : "0%", transition: idx === i ? "none" : "width 200ms linear" }} />
            </div>
          ))}
        </div>

        {/* Top controls */}
        <div className="absolute top-4 right-3 z-30 flex items-center gap-1 mt-3">
          <button onClick={() => setPaused((p) => !p)} className="p-2 rounded-full hover:bg-white/10 text-white/80" data-testid="wrap-pause" aria-label={paused ? "Play" : "Pause"}>
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-white/80" data-testid="wrap-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tap layer + slide content */}
        <div
          className="absolute inset-0 z-10 select-none"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          style={{ touchAction: "none" }}
        >
          <div key={i} className="wrap-slide h-full w-full flex flex-col justify-center px-8 py-16 text-white">
            {cur?.type === "intro" ? (
              <IntroSlide data={data} />
            ) : cur?.type === "summary" ? (
              <SummarySlide data={data} onClose={onClose} />
            ) : (
              <StatSlide slide={cur} />
            )}
          </div>
        </div>

        {/* Next hint (non-summary) */}
        {!isSummary && (
          <div className="absolute bottom-5 left-0 right-0 z-20 flex justify-center pointer-events-none">
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-white/45">
              Tap to continue <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes wrapIn { from { opacity: 0; transform: translateY(18px) scale(0.985); } to { opacity: 1; transform: none; } }
        .wrap-slide { animation: wrapIn 460ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>
    </div>
  );
  return createPortal(node, document.body);
}

function Eyebrow({ children }) {
  return <div className="text-[12px] font-semibold uppercase tracking-[0.28em] text-[#d8b877] mb-4">{children}</div>;
}

function IntroSlide({ data }) {
  return (
    <div>
      <img src="/brand/logo-white.png" alt="" className="h-12 w-12 object-contain mb-8 opacity-90"
           onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <Eyebrow>African Polistas</Eyebrow>
      <h1 className="font-serif leading-[0.95]" style={{ fontSize: "3.6rem" }}>Your week,<br />wrapped.</h1>
      <p className="mt-5 text-lg text-white/70">{data.week_label}</p>
      <p className="mt-10 text-sm text-white/50">A look back at everything that happened around the yard.</p>
    </div>
  );
}

function StatSlide({ slide }) {
  const value = slide.value_str != null ? slide.value_str : slide.value;
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <div className="text-white/85 font-serif" style={{ fontSize: "2.2rem", lineHeight: 1.05 }}>{slide.title}</div>
      <div className="mt-6 flex items-end gap-3">
        <span className="font-serif text-[#f7f4ed] tabular-nums" style={{ fontSize: "6rem", lineHeight: 0.9 }}>{value}</span>
        {slide.unit && <span className="text-white/60 text-lg pb-3">{slide.unit}</span>}
      </div>
      {slide.caption && <p className="mt-8 text-[15px] text-white/70 max-w-[85%]">{slide.caption}</p>}
    </div>
  );
}

function SummarySlide({ data, onClose }) {
  const [busy, setBusy] = useState(null);
  const a = data.summary || {};
  const rows = [];
  if (a.rides_total > 0) rows.push(["Sessions ridden", a.rides_total]);
  if (a.most_ridden) rows.push(["Most ridden", `${a.most_ridden.name}`]);
  if (a.top_horse) rows.push(["Top horse", `${a.top_horse.name}`]);
  if (a.new_horses?.count > 0) rows.push(["New horses", a.new_horses.count]);
  if (a.moves > 0) rows.push(["Yard moves", a.moves]);
  if (a.recoveries > 0) rows.push(["Recoveries", a.recoveries]);
  if (a.spend?.count > 0) rows.push(["Invoices", a.spend.count]);
  if (a.hoof_watch?.count > 0) rows.push(["Hoof watch", a.hoof_watch.count]);

  const run = async (kind) => {
    if (busy) return;
    setBusy(kind);
    try {
      const mod = await import("@/lib/weeklyWrapExports");
      if (kind === "image") await mod.downloadWeeklyImage(data);
      else await mod.downloadWeeklyPdf(data);
      toast.success(`Weekly summary ${kind === "image" ? "image" : "PDF"} downloaded`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the download");
    } finally { setBusy(null); }
  };

  return (
    <div className="h-full flex flex-col justify-center">
      <Eyebrow>That's a wrap</Eyebrow>
      <h2 className="font-serif leading-[0.95]" style={{ fontSize: "2.6rem" }}>Your week in review</h2>
      <p className="mt-2 text-white/60 text-sm">{data.week_label}</p>

      <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3" data-testid="wrap-summary-grid">
        {rows.length === 0 ? (
          <p className="text-white/60 text-sm col-span-2">A quiet week around the yard.</p>
        ) : rows.map(([label, val]) => (
          <div key={label} className="border-t border-white/15 pt-2">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">{label}</div>
            <div className="font-serif text-2xl text-[#f7f4ed] truncate">{val}</div>
          </div>
        ))}
      </div>

      <div className="mt-9 flex flex-col gap-2.5">
        <button onClick={() => run("image")} disabled={!!busy}
          className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium disabled:opacity-60"
          style={{ background: "#f7f4ed", color: "#171511" }} data-testid="wrap-download-image">
          {busy === "image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download as image
        </button>
        <button onClick={() => run("pdf")} disabled={!!busy}
          className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium border border-white/25 text-white disabled:opacity-60"
          data-testid="wrap-download-pdf">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download as PDF
        </button>
        <button onClick={onClose} className="mt-1 text-sm text-white/60 hover:text-white py-1" data-testid="wrap-done">
          Done
        </button>
      </div>
    </div>
  );
}
